import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../../../components/Spinner';
import Button from '../../../../../components/Button';
import useFetch from '../../../../../hooks/useFetch';
import DashboardService from '../../../../../services/dashboard.service';
import { formatNumber } from '../../../../../utils/methods';
import { MonthYearValue } from './MonthYearFilter';
import { ViewMode } from './ViewModeToggle';

interface TopSKUsProps {
  period: MonthYearValue;
  viewMode: ViewMode;
}

interface SKURow {
  product_id: number;
  product_name: string;
  issued_qty: number;
  issued_qty_value: number;
  closing_qty: number;
  closing_qty_value: number;
}

type DisplayMode = 'podium' | 'leaderboard';

const RANK_COLORS = ['#ffd700', '#c0c0c0', '#cd7f32', '#696cff', '#009688'];
const RANK_BG = ['rgba(255,215,0,0.08)', 'rgba(192,192,192,0.08)', 'rgba(205,127,50,0.08)', 'transparent', 'transparent'];

const TopSKUs: React.FC<TopSKUsProps> = ({ period, viewMode }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SKURow[]>([]);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('podium');

  const { loading } = useFetch({
    service: DashboardService,
    func: 'topSKUs',
    payload: { month: period.month, year: period.year },
    dependency: [period.month, period.year],
    onSuccess: (response: SKURow[]) => {
      setRows(response || []);
    },
  });

  const secondaryValue = (row: SKURow) =>
    viewMode === 'value' ? row.issued_qty_value : row.issued_qty;

  const closingValue = (row: SKURow) =>
    viewMode === 'value' ? row.closing_qty_value : row.closing_qty;

  const maxSecondary = rows.length > 0 ? Math.max(...rows.map(secondaryValue)) : 1;

  const spinner = (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 260 }}>
      <Spinner variant="primary" />
    </div>
  );

  const noData = (
    <div className="d-flex justify-content-center align-items-center text-muted" style={{ minHeight: 260 }}>
      {t('dashboard.pageTexts.topSKUs.noData')}
    </div>
  );

  const podiumView = (
    <div className="d-flex flex-column gap-2">
      {rows.map((row, index) => {
        const secondary = secondaryValue(row);
        const closing = closingValue(row);
        const rankColor = RANK_COLORS[index];

        return (
          <div
            key={row.product_id}
            className="d-flex align-items-center gap-3 rounded px-3 py-2"
            style={{
              borderLeft: `4px solid ${rankColor}`,
              background: RANK_BG[index],
            }}
          >
            <span
              className="d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: rankColor,
                color: index === 0 ? '#5d4037' : index <= 2 ? '#444' : '#fff',
                fontSize: 11,
              }}
            >
              {index + 1}
            </span>
            <span className="fw-semibold text-truncate flex-grow-1 small" title={row.product_name}>
              {row.product_name}
            </span>
            <div className="d-flex gap-3 flex-shrink-0 text-end" style={{ fontSize: 12 }}>
              <div>
                <div className="fw-semibold text-body">{formatNumber(secondary)}</div>
                <div className="text-muted" style={{ fontSize: 10 }}>{t('dashboard.pageTexts.topSKUs.secondary')}</div>
              </div>
              <div style={{ width: 1, background: '#e0e0e0' }} />
              <div>
                <div className="fw-semibold text-body">{formatNumber(closing)}</div>
                <div className="text-muted" style={{ fontSize: 10 }}>{t('dashboard.pageTexts.topSKUs.closing')}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const leaderboardView = (
    <div className="d-flex flex-column gap-4">
      {rows.map((row, index) => {
        const secondary = secondaryValue(row);
        const closing = closingValue(row);
        const barPct = maxSecondary > 0 ? (secondary / maxSecondary) * 100 : 0;

        return (
          <div key={row.product_id}>
            <div className="d-flex align-items-center gap-3 mb-2">
              <span
                className="d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: RANK_COLORS[index],
                  color: index === 0 ? '#5d4037' : index <= 2 ? '#444' : '#fff',
                  fontSize: 12,
                }}
              >
                {index + 1}
              </span>
              <span className="fw-semibold text-truncate flex-grow-1" title={row.product_name}>
                {row.product_name}
              </span>
              <div className="d-flex gap-3 flex-shrink-0 small text-muted">
                <span>
                  <span className="text-body fw-semibold">{formatNumber(secondary)}</span>
                  <span className="ms-1">{t('dashboard.pageTexts.topSKUs.secondary')}</span>
                </span>
                <span>|</span>
                <span>
                  <span className="text-body fw-semibold">{formatNumber(closing)}</span>
                  <span className="ms-1">{t('dashboard.pageTexts.topSKUs.closing')}</span>
                </span>
              </div>
            </div>
            <div className="rounded" style={{ height: 6, background: '#e8e8e8', overflow: 'hidden' }}>
              <div
                className="rounded"
                style={{
                  height: '100%',
                  width: `${barPct}%`,
                  background: RANK_COLORS[index],
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="col-12 col-xl-6">
      <div className="card h-100">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="card-title mb-0">{t('dashboard.pageTexts.topSKUs.title')}</h5>
          <div className="btn-group" role="group">
            <Button
              className={`btn btn-sm btn-icon ${displayMode === 'podium' ? 'btn-primary' : 'btn-outline-primary'}`}
              icon={<i className="ri-medal-line ri-20px" />}
              onClick={() => setDisplayMode('podium')}
            />
            <Button
              className={`btn btn-sm btn-icon ${displayMode === 'leaderboard' ? 'btn-primary' : 'btn-outline-primary'}`}
              icon={<i className="ri-bar-chart-horizontal-line ri-20px" />}
              onClick={() => setDisplayMode('leaderboard')}
            />
          </div>
        </div>
        <div className="card-body">
          {loading
            ? spinner
            : rows.length === 0
            ? noData
            : displayMode === 'podium'
            ? podiumView
            : leaderboardView}
        </div>
      </div>
    </div>
  );
};

export default TopSKUs;
